import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { conceptsPropertyContext } from '@/pages/property/concepts'
import { contractsPropertyContext } from '@/pages/property/contracts'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Skeleton
} from '@chakra-ui/react'
import { CheckboxFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsPagedQuery } from 'BrokerTsLib/src/dtos'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useContext } from 'react'
import { InputGroup } from '../../components/InputGroup'
import { useAuth } from '../../context/AuthContext'
import { usePartner } from '../../context/PartnerContext'
import { useInsurers } from '../../hooks'
import { Search } from '../../images/Search'
import { SearchWrapper } from './SearchWrapper'
import { OrganizationUserFormControls } from './shared/OrganizationUserFormControls'

interface PropertySearchFormProps {
  onSubmit: (search: InsuranceParamsPagedQuery) => void
  filter: InsuranceParamsPagedQuery
  listType: 'contracted' | 'opened'
}

export const PropertySearchForm = ({
  onSubmit,
  filter,
  listType,
}: PropertySearchFormProps) => {
  const { setCurrentPage, loading: loadingList } = useContext(
    listType == 'contracted' ? contractsPropertyContext : conceptsPropertyContext,
  )

  const methods = useForm<InsuranceParamsPagedQuery>({
    defaultValues: filter,
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = methods
  const ouId = watch('ouId')

  const { data: allInsurers, isFetching: isFetchingInsurers } = useInsurers(
    null,
    listType == 'contracted',
  )

  const { user } = useAuth()
  const { currentPartner } = usePartner()

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit((filter) => {
          setCurrentPage(1)
          return onSubmit(filter)
        })}
      >
        <SearchWrapper>
          <FormControl variant="fullWidth">
            <FormLabel htmlFor="offerNumber">Číslo nabídky</FormLabel>
            <InputGroup
              id="offerNumber"
              placeholder="Např: 73622"
              {...register('insuranceParamsId')}
            />
          </FormControl>

          {listType == 'contracted' && (
            <>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="contractNumber">Číslo smlouvy</FormLabel>
                <InputGroup
                  id="contractNumber"
                  placeholder="Např: 73622"
                  {...register('contractNumber')}
                />
              </FormControl>

              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateFrom">Datum sjednání od</FormLabel>
                <Datepicker name="dateContractedFrom" />
              </FormControl>

              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateTo">Datum sjednání do</FormLabel>
                <Datepicker name="dateContractedTo" />
              </FormControl>
            </>
          )}

          {listType == 'opened' && (
            <>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateFrom">Datum od</FormLabel>
                <Datepicker name="dateCreatedFrom" />
              </FormControl>

              <FormControl variant="fullWidth">
                <FormLabel htmlFor="dateTo">Datum do</FormLabel>
                <Datepicker name="dateCreatedTo" />
              </FormControl>
            </>
          )}

          {(allInsurers?.data.length ?? 0) > 0 && (
            <FormControl variant="fullWidth">
              <FormLabel htmlFor="insuranceCompany">Pojišťovna</FormLabel>

              <Skeleton isLoaded={!isFetchingInsurers}>
                <Select {...register('insurerId')}>
                  <option value="">Vyberte</option>
                  {allInsurers?.data.map((company) => {
                    return (
                      <option key={company.id} value={company.id.toString()}>
                        {company.description}
                      </option>
                    )
                  })}
                </Select>
              </Skeleton>
            </FormControl>
          )}

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="insureeName">
              Pojistník (příjmení/název firmy)
            </FormLabel>

            <InputGroup
              id="insureeName"
              placeholder="Např: Novák"
              {...register('legalPersonName')}
            />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="insureeNumber">
              Pojistník (rodné číslo/IČO)
            </FormLabel>

            <InputGroup
              id="insureeNumber"
              placeholder="Např: 9001012233"
              {...register('legalPersonIdentification')}
            />
          </FormControl>

          <OrganizationUserFormControls />

          {user?.isSupervisor &&
            currentPartner &&
            currentPartner.partner! == 'Kapitol' && (
            <>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="userCity">Obec uživatele</FormLabel>

                <InputGroup id="userCity" {...register('userCity')} />
              </FormControl>
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="userStreet">Ulice uživatele</FormLabel>

                <InputGroup id="userStreet" {...register('userStreet')} />
              </FormControl>
            </>
          )}

          {user?.adminCebiaRights &&
            <Flex alignItems='center'>
              <CheckboxFormControl control={methods.control} name={'showDeleted'} label='Zahrnout smazané' />
            </Flex>
          }

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gridColumn: { base: '', md: '1 / 5' },
            }}
          >
            <Button
              type="submit"
              colorScheme="primary"
              isLoading={isSubmitting || loadingList}
            >
              <Search />
              Vyhledat
            </Button>
          </Box>
        </SearchWrapper>
      </form>
    </FormProvider>
  )
}
